import { Cloud, ChevronRight, Loader2, Check } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../../../ui/common-ui/button';
import { useGraphStore } from '../store/graphStore';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuGroup,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../../../ui/common-ui/dropdown-menu';
import { useCloudAccounts } from '../hooks/useCloudAccounts';
import { useUICollapse } from '../hooks/useUICollapse';
import type { CloudType } from '../types/api';
import { cn } from '../lib/utils';

// Import cloud logos
import awsLogo from '../assets/aws-logo.svg';
import gcpLogo from '../assets/gcp-logo.svg';
import azureLogo from '../assets/azure-logo.svg';

interface CloudSelectorProps {
  onCloudChange?: (cloudType: CloudType, cloudId: string) => void;
}

export function CollapsibleCloudSelector({ onCloudChange }: CloudSelectorProps) {
  const { isCollapsed, setHoveredElement, hoveredElement } = useUICollapse();
  const [open, setOpen] = useState(false);

  const isHovered = hoveredElement === 'cloud';
  const showExpanded = !isCollapsed || isHovered || open;

  const { setFilters } = useGraphStore();
  const { accounts, selectedCloud, isLoadingAccounts, selectCloudAccount, getDisplayName } = useCloudAccounts();

  const handleAccountSelect = (cloudType: CloudType, cloudId: string) => {
    const providerKey = cloudType.toLowerCase() as 'aws' | 'gcp' | 'azure';
    setFilters({ providers: [providerKey], query: cloudId });
    selectCloudAccount(cloudType, cloudId);

    if (onCloudChange) {
      onCloudChange(cloudType, cloudId);
    }
  };

  const displayName = getDisplayName(selectedCloud.cloudType, selectedCloud.cloudId);
  const truncatedName = displayName.length > 28 ? `${displayName.slice(0, 26)}...` : displayName;

  const getProviderConfig = () => {
    const type = selectedCloud.cloudType?.toUpperCase();
    if (type === 'AWS') return { logo: awsLogo, name: 'AWS' };
    if (type === 'GCP') return { logo: gcpLogo, name: 'GCP' };
    if (type === 'AZURE') return { logo: azureLogo, name: 'Azure' };
    return { logo: null, name: 'Cloud' };
  };

  const config = getProviderConfig();

  // Force-close dropdown when the graph collapses the UI
  useEffect(() => {
    if (isCollapsed) setOpen(false);
  }, [isCollapsed]);

  return (
    <div
      className={cn(
        "glass-panel shadow-lg border border-border/50 overflow-hidden transition-[width,border-radius] duration-300 ease-out",
        showExpanded ? "w-80 rounded-2xl" : "w-14 rounded-xl"
      )}
      onMouseEnter={() => setHoveredElement('cloud')}
      onMouseLeave={() => {
        if (!open) setHoveredElement(null);
      }}
    >
      <DropdownMenu
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (next) setHoveredElement('cloud');
        }}
      >
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className={cn(
              "w-full h-14 px-2 overflow-hidden hover:bg-muted/50 transition-colors relative !justify-start",
              showExpanded ? "rounded-2xl" : "rounded-xl"
            )}
          >
            {/* Logo is always anchored to the left to avoid shifting on hover */}
            <div className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-xl bg-muted/50 border border-border/50 flex items-center justify-center">
              {config.logo ? (
                <img src={config.logo} alt={config.name} className="w-5 h-5" />
              ) : (
                <Cloud className="w-5 h-5 text-muted-foreground" />
              )}
            </div>

            {/* Text is absolutely positioned so logo never shifts due to layout reflow */}
            <div
              className={cn(
                "absolute left-14 right-10 top-1/2 -translate-y-1/2 overflow-hidden transition-opacity duration-200 ease-out",
                showExpanded ? "opacity-100" : "opacity-0 pointer-events-none"
              )}
            >
              <div className="flex flex-col items-start min-w-0 text-left">
                <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider text-left">
                  {config.name}
                </span>
                <span className="text-sm font-semibold text-foreground mt-0.5 truncate w-full text-left">
                  {truncatedName || 'Select Account'}
                </span>
              </div>
            </div>


            <ChevronRight
              className={cn(
                "absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground transition-opacity duration-200",
                showExpanded ? "opacity-100" : "opacity-0"
              )}
            />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          className="w-80 rounded-xl bg-card shadow-xl border border-border p-1"
          side="right"
          align="start"
          sideOffset={8}
        >
          <DropdownMenuGroup>
            {/* AWS */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="rounded-lg cursor-pointer py-2.5 px-3 hover:bg-muted">
                <div className="flex items-center gap-3 flex-1">
                  <img src={awsLogo} alt="AWS" className="w-5 h-5" />
                  <div className="flex flex-col">
                    <span className="font-medium text-foreground">Amazon Web Services</span>
                    <span className="text-[10px] text-muted-foreground">{accounts.aws.length} accounts</span>
                  </div>
                </div>
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent sideOffset={8} className="w-80 bg-card rounded-xl shadow-xl border border-border p-1 max-h-72 overflow-y-auto">
                  <DropdownMenuLabel className="text-[10px] text-muted-foreground uppercase tracking-wider px-3 py-2">
                    AWS Accounts
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {isLoadingAccounts ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : accounts.aws.length === 0 ? (
                    <div className="py-4 px-3 text-xs text-muted-foreground text-center">
                      No AWS accounts found
                    </div>
                  ) : (
                    accounts.aws.map((account) => {
                      const isSelected = selectedCloud.cloudType === 'AWS' && selectedCloud.cloudId === account.accountId;
                      return (
                        <DropdownMenuItem
                          key={account.accountId}
                          onSelect={() => handleAccountSelect('AWS', account.accountId)}
                          className={`py-2.5 px-3 rounded-lg cursor-pointer text-foreground ${isSelected ? 'bg-primary/10' : 'hover:bg-muted'
                            }`}
                        >
                          <div className="flex flex-col flex-1">
                            <span className="font-medium">{account.accountName}</span>
                            <span className="text-[10px] text-muted-foreground font-mono">{account.accountId}</span>
                          </div>
                          {isSelected && <Check className="w-4 h-4 ml-2 text-primary" />}
                        </DropdownMenuItem>
                      );
                    })
                  )}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>

            {/* GCP */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="rounded-lg cursor-pointer py-2.5 px-3 hover:bg-muted">
                <div className="flex items-center gap-3 flex-1">
                  <img src={gcpLogo} alt="GCP" className="w-5 h-5" />
                  <div className="flex flex-col">
                    <span className="font-medium text-foreground">Google Cloud Platform</span>
                    <span className="text-[10px] text-muted-foreground">{accounts.gcp.length} projects</span>
                  </div>
                </div>
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent sideOffset={8} className="w-80 bg-card rounded-xl shadow-xl border border-border p-1 max-h-72 overflow-y-auto">
                  <DropdownMenuLabel className="text-[10px] text-muted-foreground uppercase tracking-wider px-3 py-2">
                    GCP Projects
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {isLoadingAccounts ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : accounts.gcp.length === 0 ? (
                    <div className="py-4 px-3 text-xs text-muted-foreground text-center">
                      No GCP projects found
                    </div>
                  ) : (
                    accounts.gcp.map((projectId) => {
                      const isSelected = selectedCloud.cloudType === 'GCP' && selectedCloud.cloudId === projectId;
                      return (
                        <DropdownMenuItem
                          key={projectId}
                          onSelect={() => handleAccountSelect('GCP', projectId)}
                          className={`font-mono text-xs py-2.5 px-3 rounded-lg cursor-pointer text-foreground ${isSelected ? 'bg-primary/10' : 'hover:bg-muted'
                            }`}
                        >
                          <span className="flex-1">{projectId}</span>
                          {isSelected && <Check className="w-4 h-4 ml-2 text-primary" />}
                        </DropdownMenuItem>
                      );
                    })
                  )}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>

            {/* Azure */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="rounded-lg cursor-pointer py-2.5 px-3 hover:bg-muted">
                <div className="flex items-center gap-3 flex-1">
                  <img src={azureLogo} alt="Azure" className="w-5 h-5" />
                  <div className="flex flex-col">
                    <span className="font-medium text-foreground">Microsoft Azure</span>
                    <span className="text-[10px] text-muted-foreground">{accounts.azure.length} subscriptions</span>
                  </div>
                </div>
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent sideOffset={8} className="w-80 bg-card rounded-xl shadow-xl border border-border p-1 max-h-72 overflow-y-auto">
                  <DropdownMenuLabel className="text-[10px] text-muted-foreground uppercase tracking-wider px-3 py-2">
                    Azure Subscriptions
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {isLoadingAccounts ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : accounts.azure.length === 0 ? (
                    <div className="py-4 px-3 text-xs text-muted-foreground text-center">
                      No Azure subscriptions found
                    </div>
                  ) : (
                    accounts.azure.map((subscription) => {
                      const isSelected = selectedCloud.cloudType === 'AZURE' && selectedCloud.cloudId === subscription.subscriptionId;
                      return (
                        <DropdownMenuItem
                          key={subscription.subscriptionId}
                          onSelect={() => handleAccountSelect('AZURE', subscription.subscriptionId)}
                          className={`py-2.5 px-3 rounded-lg cursor-pointer text-foreground ${isSelected ? 'bg-primary/10' : 'hover:bg-muted'
                            }`}
                        >
                          <div className="flex flex-col flex-1">
                            <span className="font-medium">{subscription.subscriptionName}</span>
                            <span className="text-[10px] text-muted-foreground font-mono">{subscription.subscriptionId}</span>
                          </div>
                          {isSelected && <Check className="w-4 h-4 ml-2 text-primary" />}
                        </DropdownMenuItem>
                      );
                    })
                  )}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
